import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import {
  CartItem,
  CustomerInfo,
  CartContextType,
  CheckoutStep,
  PersistedCartState,
  ACTIVITIES_REQUIRING_WEIGHT
} from '@/types/cart.types'

const CART_STORAGE_KEY = 'terra-lao-cart'
const CART_VERSION = 1

const CartContext = createContext<CartContextType | undefined>(undefined)

// Generate UUID for cart items
const generateId = (): string => {
  return crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([])
  const [customerInfo, setCustomerInfoState] = useState<CustomerInfo | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>('cart')
  const [initialized, setInitialized] = useState(false)

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY)
      if (stored) {
        const parsed: PersistedCartState = JSON.parse(stored)
        // Version check for future migrations
        if (parsed.version === CART_VERSION) {
          setItems(parsed.items || [])
          setCustomerInfoState(parsed.customerInfo || null)
        }
      }
    } catch (e) {
      console.error('Failed to load cart from localStorage:', e)
    }
    setInitialized(true)
  }, [])

  // Persist cart to localStorage on changes
  useEffect(() => {
    if (!initialized) return

    try {
      const cartState: PersistedCartState = {
        items,
        customerInfo,
        version: CART_VERSION,
        lastUpdated: Date.now()
      }
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartState))
    } catch (e) {
      console.error('Failed to save cart to localStorage:', e)
    }
  }, [items, customerInfo, initialized])

  // Add item to cart
  const addItem = useCallback((item: Omit<CartItem, 'id' | 'addedAt' | 'subtotal'>) => {
    console.log('CartContext.addItem called with:', item)
    // Calculate subtotal: base price + transport if applicable
    const baseSubtotal = item.pricePerPerson * item.partySize
    const transportPrice = item.transportOption?.enabled ? item.transportOption.price : 0

    const newItem: CartItem = {
      ...item,
      id: generateId(),
      addedAt: Date.now(),
      subtotal: baseSubtotal + transportPrice
    }
    console.log('CartContext: New item created:', newItem)

    setItems(prev => {
      const newItems = [...prev, newItem]
      console.log('CartContext: Items updated, total items:', newItems.length)
      return newItems
    })
  }, [])

  // Remove item from cart
  const removeItem = useCallback((id: string) => {
    setItems(prev => prev.filter(item => item.id !== id))
  }, [])

  // Update item in cart
  const updateItem = useCallback((id: string, updates: Partial<CartItem>) => {
    setItems(prev => prev.map(item => {
      if (item.id !== id) return item

      const updated = { ...item, ...updates }
      // Recalculate subtotal if price, party size, or transport changed
      if ('pricePerPerson' in updates || 'partySize' in updates || 'transportOption' in updates) {
        const baseSubtotal = updated.pricePerPerson * updated.partySize
        const transportPrice = updated.transportOption?.enabled ? updated.transportOption.price : 0
        updated.subtotal = baseSubtotal + transportPrice
      }
      return updated
    }))
  }, [])

  // Clear all items from cart
  const clearCart = useCallback(() => {
    setItems([])
    setCheckoutStep('cart')
  }, [])

  // Set customer info
  const setCustomerInfo = useCallback((info: CustomerInfo) => {
    setCustomerInfoState(info)
  }, [])

  // Clear customer info
  const clearCustomerInfo = useCallback(() => {
    setCustomerInfoState(null)
  }, [])

  // UI actions
  const openCart = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closeCart = useCallback(() => {
    setIsOpen(false)
    // Reset to cart step when closing (optional - could keep state)
    // setCheckoutStep('cart')
  }, [])

  const toggleCart = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  // Get total item count
  const getItemCount = useCallback(() => {
    return items.reduce((count, item) => count + item.partySize, 0)
  }, [items])

  // Get cart total
  const getTotal = useCallback(() => {
    return items.reduce((total, item) => total + item.subtotal, 0)
  }, [items])

  // Check if any flight activities are in cart
  const hasFlightActivities = useCallback(() => {
    return items.some(item =>
      ACTIVITIES_REQUIRING_WEIGHT.includes(item.activityType)
    )
  }, [items])

  // Check if cart is empty
  const isEmpty = useCallback(() => {
    return items.length === 0
  }, [items])

  return (
    <CartContext.Provider value={{
      // State
      items,
      customerInfo,
      isOpen,
      checkoutStep,

      // Cart actions
      addItem,
      removeItem,
      updateItem,
      clearCart,

      // Customer info
      setCustomerInfo,
      clearCustomerInfo,

      // UI actions
      openCart,
      closeCart,
      toggleCart,
      setCheckoutStep,

      // Computed
      getItemCount,
      getTotal,
      hasFlightActivities,
      isEmpty
    }}>
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
